import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { 
  Activity, ArrowLeft, Send, AlertTriangle, FileText, User, Calendar, Clock, X, AlertCircle,
  Paperclip, Download, Upload, Image, File, Trash2, Info, ExternalLink, ZoomIn, ChevronLeft, ChevronRight,
  MessageSquarePlus, CreditCard, Timer, History, Copy, CheckCircle2, Ban, Mail, Receipt
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import PatientHistoryPanel from "@/components/PatientHistoryPanel";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Escalation Modal Component
const EscalationModal = ({ isOpen, onClose, consultId, onSuccess }) => {
  const [escalationData, setEscalationData] = useState({
    escalation_type: "",
    urgency_level: "",
    clinical_reason: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Editable contact resources
  const [contactChecked, setContactChecked] = useState({ whatsapp_fleni: false });
  const [customContact, setCustomContact] = useState({ label: "", value: "" });
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  // Load doctor's saved contact preferences
  useEffect(() => {
    if (isOpen && !prefsLoaded) {
      try {
        const saved = localStorage.getItem('doctor:custom_escalation_contact');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed && typeof parsed === 'object') {
            setCustomContact({
              label: parsed.label || "",
              value: parsed.value || ""
            });
          }
        }
      } catch {
        // ignore corrupted localStorage
      }
      setPrefsLoaded(true);
    }
    if (!isOpen) setPrefsLoaded(false);
  }, [isOpen, prefsLoaded]);

  const escalationTypes = [
    { value: "presencial", label: "Requiere evaluación presencial" },
    { value: "teleconsulta", label: "Requiere teleconsulta sincrónica" },
    { value: "estudios", label: "Requiere estudios complementarios" },
    { value: "especialista", label: "Derivación a especialista" },
    { value: "complejidad", label: "Complejidad clínica (no resoluble por consulta asincrónica)" }
  ];

  const urgencyLevels = [
    { value: "critica", label: "Inmediata", timeframe: "inmediata", color: "text-red-600" },
    { value: "alta", label: "Dentro de 24 horas", timeframe: "24h", color: "text-orange-600" },
    { value: "media", label: "Dentro de 72 horas", timeframe: "72h", color: "text-yellow-600" },
    { value: "baja", label: "Programable", timeframe: "programable", color: "text-blue-600" }
  ];

  const getTimeframeFromUrgency = (urgencyLevel) => {
    const level = urgencyLevels.find(l => l.value === urgencyLevel);
    return level ? level.timeframe : null;
  };

  const handleSubmit = async () => {
    if (!escalationData.escalation_type) {
      toast.error("Selecciona el tipo de escalamiento");
      return;
    }
    if (!escalationData.urgency_level) {
      toast.error("Selecciona el nivel de urgencia");
      return;
    }
    if (escalationData.clinical_reason.length < 20) {
      toast.error("El motivo clínico debe tener al menos 20 caracteres");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('auth_token');

      // Build resolved contact resources
      const resolvedResources = [];
      if (contactChecked.whatsapp_fleni) {
        resolvedResources.push({ label: "WhatsApp Fleni — Turnos, consultas, recetas y otras gestiones", value: "https://wa.me/5491156551155", type: "whatsapp" });
      }
      // Custom contact: included if doctor filled in a value (label is optional)
      const customValue = customContact.value.trim();
      const customLabel = customContact.label.trim();
      if (customValue) {
        resolvedResources.push({
          label: customLabel || "Contacto del médico",
          value: customValue,
          type: "custom"
        });
        // Persist for future escalations (so doctor doesn't retype every time)
        try {
          localStorage.setItem(
            'doctor:custom_escalation_contact',
            JSON.stringify({ label: customLabel, value: customValue })
          );
        } catch {
          // ignore quota errors
        }
      }

      const payload = {
        ...escalationData,
        recommended_timeframe: getTimeframeFromUrgency(escalationData.urgency_level),
        contact_resources: resolvedResources
      };
      
      const response = await fetch(`${API_URL}/api/consults/${consultId}/escalate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("Consulta escalada correctamente");
        onSuccess(data);
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al escalar la consulta");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Error al conectar con el servidor");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="escalation-modal">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">Escalar Consulta</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <Label className="text-base font-semibold mb-3 block">Tipo de escalamiento *</Label>
            <div className="space-y-2">
              {escalationTypes.map((type) => (
                <label
                  key={type.value}
                  className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                    escalationData.escalation_type === type.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="escalation_type"
                    value={type.value}
                    checked={escalationData.escalation_type === type.value}
                    onChange={(e) => setEscalationData({ ...escalationData, escalation_type: e.target.value })}
                    className="mr-3"
                    data-testid={`escalation-type-${type.value}`}
                  />
                  <span className="text-slate-700">{type.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-base font-semibold mb-3 block">Nivel de urgencia *</Label>
            <div className="space-y-2">
              {urgencyLevels.map((level) => (
                <label
                  key={level.value}
                  className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                    escalationData.urgency_level === level.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="urgency_level"
                    value={level.value}
                    checked={escalationData.urgency_level === level.value}
                    onChange={(e) => setEscalationData({ ...escalationData, urgency_level: e.target.value })}
                    className="mr-3"
                    data-testid={`urgency-level-${level.value}`}
                  />
                  <span className={`font-medium ${level.color}`}>{level.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-base font-semibold mb-3 block">Motivo clínico *</Label>
            <Textarea
              value={escalationData.clinical_reason}
              onChange={(e) => setEscalationData({ ...escalationData, clinical_reason: e.target.value })}
              placeholder="Describe el motivo clínico por el cual esta consulta debe ser escalada (mínimo 20 caracteres)..."
              rows={4}
              className="resize-none"
              data-testid="clinical-reason-textarea"
            />
            <p className={`text-xs mt-1 ${escalationData.clinical_reason.length < 20 ? 'text-red-500' : 'text-slate-500'}`}>
              {escalationData.clinical_reason.length}/20 caracteres mínimos
            </p>
          </div>

          {/* Contact resources for patient */}
          <div>
            <Label className="text-sm font-semibold mb-2 block text-slate-700">Incluir datos de contacto para el paciente</Label>
            <p className="text-xs text-slate-400 mb-3">Opcional. Se enviarán junto con la notificación de escalamiento.</p>
            <div className="space-y-2">
              {/* WhatsApp Fleni Bot */}
              <label
                className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                  contactChecked.whatsapp_fleni ? 'border-[#25D366] bg-green-50/50' : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={contactChecked.whatsapp_fleni}
                  onChange={(e) => setContactChecked({ ...contactChecked, whatsapp_fleni: e.target.checked })}
                  className="mr-3 h-4 w-4 rounded"
                  data-testid="resource-whatsapp_fleni"
                />
                <div>
                  <span className="text-sm text-slate-700 font-medium">WhatsApp Fleni</span>
                  <p className="text-xs text-slate-400">Turnos, consultas, recetas y otras gestiones</p>
                  <span className="text-xs text-green-600 font-medium">+54 9 11 5655-1155</span>
                </div>
              </label>

              {/* Custom contact (free-text, editable by doctor) */}
              <div className="p-3 rounded-lg border border-slate-200 bg-slate-50/50 space-y-2">
                <p className="text-sm text-slate-700 font-medium">Contacto personalizado</p>
                <p className="text-xs text-slate-400 -mt-1">
                  Opcional. Ingresá un contacto adicional (secretaría, número directo, email, etc.). Se guarda para próximos escalamientos.
                </p>
                <Input
                  type="text"
                  value={customContact.label}
                  onChange={(e) => setCustomContact({ ...customContact, label: e.target.value })}
                  placeholder="Descripción (ej: Secretaría privada)"
                  className="h-9 text-sm"
                  data-testid="custom-contact-label-input"
                  maxLength={80}
                />
                <Input
                  type="text"
                  value={customContact.value}
                  onChange={(e) => setCustomContact({ ...customContact, value: e.target.value })}
                  placeholder="Contacto (ej: +54 11 1234-5678 o email)"
                  className="h-9 text-sm"
                  data-testid="custom-contact-value-input"
                  maxLength={120}
                />
                {customContact.value.trim() && (
                  <p className="text-xs text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    Se incluirá en la notificación al paciente
                  </p>
                )}
              </div>
            </div>
          </div>

          {escalationData.urgency_level === 'critica' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">Urgencia Inmediata</p>
                <p className="text-sm text-red-700">Se notificará al paciente que debe buscar atención de forma inmediata.</p>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-slate-200 flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1" disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
            data-testid="confirm-escalation-btn"
          >
            {submitting ? "Escalando..." : "Confirmar escalamiento"}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Request Additional Info Modal Component
const RequestInfoModal = ({ isOpen, onClose, consultId, onSuccess, alreadyRequested }) => {
  const [requestText, setRequestText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (requestText.length < 10) {
      toast.error("La solicitud debe tener al menos 10 caracteres");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/consults/${consultId}/request-info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ info_request_text: requestText })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("Solicitud enviada al paciente");
        onSuccess(data);
        onClose();
        setRequestText("");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al enviar solicitud");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Error al conectar con el servidor");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="request-info-modal">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <MessageSquarePlus className="h-6 w-6 text-amber-500" />
            Solicitar Información Adicional
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Warning Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-900">Esta acción solo puede realizarse UNA vez por consulta</p>
              <p className="text-sm text-amber-700 mt-1">
                El paciente tendrá <strong>7 días</strong> para responder. Si no responde, la consulta se cerrará automáticamente.
              </p>
            </div>
          </div>

          {/* Request Text */}
          <div>
            <Label className="text-base font-semibold mb-3 block">
              ¿Qué información necesitas del paciente? *
            </Label>
            <Textarea
              value={requestText}
              onChange={(e) => setRequestText(e.target.value)}
              placeholder="Ej: Necesito conocer la dosis exacta del medicamento y hace cuánto tiempo lo tomas..."
              rows={4}
              className="resize-none"
              data-testid="info-request-textarea"
            />
            <p className={`text-xs mt-1 ${requestText.length < 10 ? 'text-red-500' : 'text-slate-500'}`}>
              {requestText.length}/10 caracteres mínimos
            </p>
          </div>

          {/* Info about payment */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-start gap-3">
            <CreditCard className="h-5 w-5 text-slate-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-slate-700">
                <strong>Pago retenido:</strong> El pago permanecerá retenido hasta que respondas la consulta o la escales.
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-slate-200 flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1" disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || requestText.length < 10}
            className="flex-1 bg-amber-500 hover:bg-amber-600"
            data-testid="confirm-request-info-btn"
          >
            {submitting ? "Enviando..." : "Enviar solicitud"}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Patient Additional Info Block (when patient has responded)
const PatientAdditionalInfoBlock = ({ consult }) => {
  if (!consult.patient_additional_info) return null;

  const receivedAt = consult.patient_info_received_at 
    ? new Date(consult.patient_info_received_at).toLocaleString('es-AR')
    : 'N/A';

  return (
    <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 mb-6">
      <h3 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
        <MessageSquarePlus className="h-5 w-5 text-purple-600" />
        Información Adicional del Paciente
        <Badge className="bg-purple-100 text-purple-700 border-purple-200 ml-2">Nueva</Badge>
      </h3>
      
      {/* Original request */}
      {consult.info_request_text && (
        <div className="mb-4">
          <p className="text-xs text-purple-600 font-medium mb-1">Tu solicitud:</p>
          <p className="text-sm text-purple-700 bg-purple-100/50 p-3 rounded italic">
            "{consult.info_request_text}"
          </p>
        </div>
      )}
      
      {/* Patient response */}
      <div>
        <p className="text-xs text-purple-600 font-medium mb-1">Respuesta del paciente ({receivedAt}):</p>
        <p className="text-slate-800 bg-white p-3 rounded border border-purple-200 whitespace-pre-wrap">
          {consult.patient_additional_info}
        </p>
      </div>
    </div>
  );
};

// Awaiting Info Status Block
const AwaitingInfoBlock = ({ consult }) => {
  const [timeRemaining, setTimeRemaining] = useState('');

  useEffect(() => {
    const calculateTimeRemaining = () => {
      if (!consult.info_request_deadline) return;
      
      const deadline = new Date(consult.info_request_deadline);
      const now = new Date();
      const diff = deadline - now;
      
      if (diff <= 0) {
        setTimeRemaining('Plazo vencido');
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      
      if (days > 0) {
        setTimeRemaining(`${days} día${days > 1 ? 's' : ''}, ${hours}h`);
      } else {
        setTimeRemaining(`${hours} hora${hours > 1 ? 's' : ''}`);
      }
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [consult.info_request_deadline]);

  if (consult.status !== 'AWAITING_INFO') return null;

  const requestedAt = consult.info_request_sent_at 
    ? new Date(consult.info_request_sent_at).toLocaleString('es-AR')
    : 'N/A';
  const deadline = consult.info_request_deadline
    ? new Date(consult.info_request_deadline).toLocaleString('es-AR')
    : 'N/A';

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
      <h3 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
        <Timer className="h-5 w-5 text-amber-600" />
        Esperando Información del Paciente
      </h3>
      
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-sm text-amber-700">Tiempo restante:</span>
          <Badge className="bg-amber-100 text-amber-800 border-amber-300 font-semibold">
            {timeRemaining}
          </Badge>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-sm text-amber-700">Solicitado:</span>
          <span className="text-sm font-medium text-amber-900">{requestedAt}</span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-sm text-amber-700">Vence:</span>
          <span className="text-sm font-medium text-amber-900">{deadline}</span>
        </div>

        <div className="pt-3 border-t border-amber-200">
          <p className="text-xs text-amber-600 font-medium mb-1">Tu solicitud:</p>
          <p className="text-sm text-amber-800 bg-amber-100/50 p-2 rounded">
            "{consult.info_request_text}"
          </p>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <CreditCard className="h-4 w-4 text-amber-600" />
          <span className="text-sm text-amber-700 font-medium">Pago retenido</span>
        </div>
      </div>
    </div>
  );
};

// Escalation Info Block Component
const EscalationBlock = ({ escalation }) => {
  const urgencyColors = {
    critica: "bg-red-100 text-red-700 border-red-200",
    alta: "bg-orange-100 text-orange-700 border-orange-200",
    media: "bg-yellow-100 text-yellow-700 border-yellow-200",
    baja: "bg-blue-100 text-blue-700 border-blue-200"
  };

  const urgencyLabels = {
    critica: "Inmediata",
    alta: "24 horas",
    media: "72 horas",
    baja: "Programable"
  };

  const typeLabels = {
    presencial: "Evaluación presencial",
    teleconsulta: "Teleconsulta sincrónica",
    estudios: "Estudios complementarios",
    especialista: "Derivación a especialista",
    complejidad: "Complejidad clínica"
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <h3 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
        <AlertCircle className="h-5 w-5 text-blue-600" />
        Escalamiento
      </h3>
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-sm text-slate-600">Tipo:</span>
          <span className="font-medium text-slate-900">{typeLabels[escalation.escalation_type] || escalation.escalation_type}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-slate-600">Urgencia:</span>
          <Badge className={`${urgencyColors[escalation.urgency_level]} border`}>
            {urgencyLabels[escalation.urgency_level] || escalation.urgency_level}
          </Badge>
        </div>
        {escalation.recommended_timeframe && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-600">Plazo:</span>
            <span className="font-medium text-slate-900 capitalize">{escalation.recommended_timeframe}</span>
          </div>
        )}
        <div className="flex justify-between items-center">
          <span className="text-sm text-slate-600">Fecha:</span>
          <span className="font-medium text-slate-900">{new Date(escalation.created_at).toLocaleString('es-AR')}</span>
        </div>
        {escalation.doctor_name && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-600">Médico:</span>
            <span className="font-medium text-slate-900">{escalation.doctor_name}</span>
          </div>
        )}
        <div className="pt-3 border-t border-slate-100">
          <span className="text-sm text-slate-600 block mb-1">Motivo:</span>
          <p className="text-sm text-slate-800 bg-slate-50 p-3 rounded">{escalation.clinical_reason}</p>
        </div>
      </div>
    </div>
  );
};

// Image Preview Modal Component
const ImagePreviewModal = ({ isOpen, onClose, imageUrl, filename, allImages, currentIndex, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setError(false);
    }
  }, [isOpen, imageUrl]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && allImages && currentIndex > 0) onNavigate(currentIndex - 1);
      if (e.key === 'ArrowRight' && allImages && currentIndex < allImages.length - 1) onNavigate(currentIndex + 1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, allImages, currentIndex, onNavigate]);

  if (!isOpen) return null;

  const handleDownload = () => {
    window.open(imageUrl, '_blank');
  };

  return (
    <div 
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4"
      onClick={onClose}
      data-testid="image-preview-modal"
    >
      {/* Close button */}
      <button 
        onClick={onClose}
        className="absolute top-4 right-4 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors z-10"
        aria-label="Cerrar"
      >
        <X className="h-8 w-8" />
      </button>

      {/* Navigation arrows */}
      {allImages && allImages.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); currentIndex > 0 && onNavigate(currentIndex - 1); }}
            className={`absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full transition-colors z-10 ${
              currentIndex > 0 ? 'text-white/70 hover:text-white hover:bg-white/10' : 'text-white/20 cursor-not-allowed'
            }`}
            disabled={currentIndex === 0}
            aria-label="Imagen anterior"
          >
            <ChevronLeft className="h-10 w-10" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); currentIndex < allImages.length - 1 && onNavigate(currentIndex + 1); }}
            className={`absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full transition-colors z-10 ${
              currentIndex < allImages.length - 1 ? 'text-white/70 hover:text-white hover:bg-white/10' : 'text-white/20 cursor-not-allowed'
            }`}
            disabled={currentIndex === allImages.length - 1}
            aria-label="Imagen siguiente"
          >
            <ChevronRight className="h-10 w-10" />
          </button>
        </>
      )}

      {/* Image container */}
      <div 
        className="relative max-w-[90vw] max-h-[85vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        {loading && !error && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          </div>
        )}
        
        {error ? (
          <div className="text-white text-center p-8">
            <AlertCircle className="h-16 w-16 mx-auto mb-4 text-red-400" />
            <p className="text-lg">No se pudo cargar la imagen</p>
            <Button 
              onClick={handleDownload}
              className="mt-4 bg-white/20 hover:bg-white/30"
            >
              <Download className="h-4 w-4 mr-2" />
              Descargar archivo
            </Button>
          </div>
        ) : (
          <img
            src={imageUrl}
            alt={filename}
            className={`max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl transition-opacity duration-300 ${loading ? 'opacity-0' : 'opacity-100'}`}
            onLoad={() => setLoading(false)}
            onError={() => { setLoading(false); setError(true); }}
          />
        )}

        {/* Footer with filename and controls */}
        <div className="mt-4 flex items-center gap-4 bg-black/50 px-4 py-2 rounded-full">
          <span className="text-white/90 text-sm truncate max-w-[300px]">{filename}</span>
          {allImages && allImages.length > 1 && (
            <span className="text-white/60 text-sm">
              {currentIndex + 1} / {allImages.length}
            </span>
          )}
          <Button 
            size="sm"
            onClick={handleDownload}
            className="bg-white/20 hover:bg-white/30 text-white border-0"
          >
            <Download className="h-4 w-4 mr-1" />
            Descargar
          </Button>
        </div>
      </div>
    </div>
  );
};

// Attachment Item Component with Image Preview
const AttachmentItem = ({ attachment, canDownload = true, onImageClick, showThumbnail = false }) => {
  const [thumbnailError, setThumbnailError] = useState(false);
  
  const isImage = attachment.file_type?.startsWith('image/') || 
                  /\.(jpg|jpeg|png|gif|webp)$/i.test(attachment.filename || attachment.original_filename || attachment.file_url);
  const isPdf = attachment.file_type === 'application/pdf' || 
                /\.pdf$/i.test(attachment.filename || attachment.original_filename || attachment.file_url);
  
  const getIcon = () => {
    if (isImage) return <Image className="h-5 w-5 text-blue-500" />;
    if (isPdf) return <FileText className="h-5 w-5 text-red-500" />;
    return <File className="h-5 w-5 text-slate-500" />;
  };

  const filename = attachment.filename || attachment.original_filename || attachment.file_url?.split('/').pop() || 'Archivo';
  
  const imageUrl = attachment.file_url?.startsWith('http') 
    ? attachment.file_url 
    : `${API_URL}${attachment.file_url}`;

  const handleDownload = (e) => {
    e?.stopPropagation();
    window.open(imageUrl, '_blank');
  };

  const handleClick = () => {
    if (isImage && onImageClick) {
      onImageClick(attachment);
    }
  };

  // Image with thumbnail preview
  if (isImage && showThumbnail && !thumbnailError) {
    return (
      <div 
        className="group cursor-pointer"
        onClick={handleClick}
        data-testid={`attachment-item-${attachment.id}`}
      >
        <div className="relative rounded-lg overflow-hidden border border-slate-200 hover:border-blue-400 transition-colors">
          {/* Thumbnail */}
          <div className="aspect-video bg-slate-100 relative">
            <img
              src={imageUrl}
              alt={filename}
              className="w-full h-full object-cover"
              onError={() => setThumbnailError(true)}
            />
            {/* Overlay on hover */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2">
                <span className="bg-white/90 text-slate-800 px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1">
                  <ZoomIn className="h-4 w-4" />
                  Ver imagen
                </span>
              </div>
            </div>
          </div>
          {/* Filename bar */}
          <div className="p-2 bg-white flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Image className="h-4 w-4 text-blue-500 flex-shrink-0" />
              <span className="text-xs text-slate-600 truncate">{filename}</span>
            </div>
            {canDownload && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleDownload}
                className="h-7 w-7 p-0 opacity-70 hover:opacity-100"
                title="Descargar"
              >
                <Download className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Standard list view (for non-images or fallback)
  return (
    <div 
      className={`flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 group hover:bg-slate-100 transition-colors ${isImage && onImageClick ? 'cursor-pointer hover:border-blue-300' : ''}`}
      onClick={isImage && onImageClick ? handleClick : undefined}
      data-testid={`attachment-item-${attachment.id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        {getIcon()}
        <span className="text-sm text-slate-700 truncate">{filename}</span>
        {isImage && onImageClick && (
          <Badge className="bg-blue-50 text-blue-600 border-blue-200 text-xs">
            <ZoomIn className="h-3 w-3 mr-1" />
            Click para ver
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-1">
        {canDownload && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleDownload}
            className="opacity-70 group-hover:opacity-100"
            title="Descargar archivo"
          >
            <Download className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

// Patient Attachments Section with Image Gallery
const PatientAttachmentsSection = ({ attachments }) => {
  const [previewImage, setPreviewImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!attachments || attachments.length === 0) return null;

  // Separate images from other files
  const imageAttachments = attachments.filter(att => 
    att.file_type?.startsWith('image/') || 
    /\.(jpg|jpeg|png|gif|webp)$/i.test(att.filename || att.original_filename || att.file_url)
  );
  const otherAttachments = attachments.filter(att => 
    !att.file_type?.startsWith('image/') && 
    !/\.(jpg|jpeg|png|gif|webp)$/i.test(att.filename || att.original_filename || att.file_url)
  );

  const handleImageClick = (attachment) => {
    const index = imageAttachments.findIndex(img => img.id === attachment.id);
    setCurrentImageIndex(index >= 0 ? index : 0);
    setPreviewImage(attachment);
  };

  const handleNavigate = (newIndex) => {
    if (newIndex >= 0 && newIndex < imageAttachments.length) {
      setCurrentImageIndex(newIndex);
      setPreviewImage(imageAttachments[newIndex]);
    }
  };

  const getImageUrl = (att) => {
    return att.file_url?.startsWith('http') ? att.file_url : `${API_URL}${att.file_url}`;
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
        <Paperclip className="h-5 w-5 text-slate-500" />
        Archivos del Paciente
        <Badge className="bg-slate-100 text-slate-600 border-0 ml-2">{attachments.length}</Badge>
      </h3>

      {/* Image Gallery Grid */}
      {imageAttachments.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-slate-500 mb-3 flex items-center gap-1">
            <Image className="h-3.5 w-3.5" />
            Imágenes ({imageAttachments.length}) - Haz clic para ampliar
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {imageAttachments.map((att, idx) => (
              <AttachmentItem 
                key={att.id || idx} 
                attachment={att} 
                onImageClick={handleImageClick}
                showThumbnail={true}
              />
            ))}
          </div>
        </div>
      )}

      {/* Other Files List */}
      {otherAttachments.length > 0 && (
        <div className={imageAttachments.length > 0 ? "pt-4 border-t border-slate-200" : ""}>
          {imageAttachments.length > 0 && (
            <p className="text-xs text-slate-500 mb-3 flex items-center gap-1">
              <FileText className="h-3.5 w-3.5" />
              Otros documentos ({otherAttachments.length})
            </p>
          )}
          <div className="space-y-2">
            {otherAttachments.map((att, idx) => (
              <AttachmentItem key={att.id || idx} attachment={att} />
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-slate-500 mt-4">
        Descarga los archivos para revisarlos. Si necesitas completar un formulario, descárgalo, complétalo y adjúntalo en tu respuesta.
      </p>

      {/* Image Preview Modal */}
      <ImagePreviewModal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageUrl={previewImage ? getImageUrl(previewImage) : ''}
        filename={previewImage?.filename || previewImage?.original_filename || 'Imagen'}
        allImages={imageAttachments}
        currentIndex={currentImageIndex}
        onNavigate={handleNavigate}
      />
    </div>
  );
};

// Doctor File Upload Component
const DoctorFileUpload = ({ files, onFilesChange, disabled }) => {
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    // Validación por extensión (el MIME de .docx/.xlsx llega vacío o inconsistente
    // según el navegador). Debe espejar ALLOWED_EXTENSIONS del backend.
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.webp', '.gif', '.doc', '.docx', '.xls', '.xlsx'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    for (const file of selectedFiles) {
      const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
      if (!allowedExtensions.includes(ext)) {
        toast.error(`Tipo de archivo no permitido: ${file.name}. Usa PDF, Word, Excel o imágenes.`);
        return;
      }
      if (file.size > maxSize) {
        toast.error(`Archivo muy grande: ${file.name}. Máximo 10MB.`);
        return;
      }
    }

    if (files.length + selectedFiles.length > 5) {
      toast.error("Máximo 5 archivos adjuntos");
      return;
    }

    onFilesChange([...files, ...selectedFiles]);
    e.target.value = ''; // Reset input
  };

  const removeFile = (index) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2">
          <Paperclip className="h-4 w-4 text-slate-500" />
          Adjuntar archivos a la respuesta
        </Label>
        <span className="text-xs text-slate-500">{files.length}/5 archivos</span>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file, idx) => (
            <div 
              key={idx} 
              className="flex items-center justify-between p-2 bg-blue-50 rounded-lg border border-blue-200"
            >
              <div className="flex items-center gap-2 min-w-0">
                {file.type?.startsWith('image/') ? (
                  <Image className="h-4 w-4 text-blue-500" />
                ) : (
                  <FileText className="h-4 w-4 text-red-500" />
                )}
                <span className="text-sm text-slate-700 truncate">{file.name}</span>
                <span className="text-xs text-slate-400">
                  ({(file.size / 1024).toFixed(0)} KB)
                </span>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => removeFile(idx)}
                disabled={disabled}
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Upload Button */}
      {files.length < 5 && (
        <label className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
          disabled 
            ? 'border-slate-200 bg-slate-50 cursor-not-allowed' 
            : 'border-slate-300 hover:border-blue-400 hover:bg-blue-50'
        }`}>
          <Upload className="h-5 w-5 text-slate-400" />
          <span className="text-sm text-slate-600">
            Adjuntar archivo (PDF, Word, Excel, JPG, PNG - máx. 10MB)
          </span>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,.xls,.xlsx"
            onChange={handleFileSelect}
            disabled={disabled}
            className="hidden"
            multiple
            data-testid="doctor-file-upload"
          />
        </label>
      )}
    </div>
  );
};

// Legacy Fleni default kept ONLY as fallback for consults whose institution predates
// the contact_info migration. Any new tenant defines its own hce_url in Institution.contact_info.
const DEFAULT_HCE_URL = "https://portalvpn.fleni.org.ar:4433/remote/login?lang=sp";
const DEFAULT_HCE_LABEL = "Abrir Historia Clínica Digital Fleni";

const RecetaActionsCard = ({ consult, institution, onResolved, onRequiresConsult }) => {
  const [resolving, setResolving] = useState(false);
  const [showReqModal, setShowReqModal] = useState(false);
  const [reqNote, setReqNote] = useState("");
  const [reqSubmitting, setReqSubmitting] = useState(false);

  const isResolved = consult.status === 'Resolved';
  const alreadyEscalatedToConsult = !!consult.requires_clinical_consult;

  // Resolve the tenant-specific HC/prescription portal. Falls back to Fleni's SIDCA
  // for backwards compatibility if the consult's institution has no contact_info.
  const hceUrl = institution?.contact_info?.hce_url || DEFAULT_HCE_URL;
  const hceOpenLabel = institution?.contact_info?.hce_url ? "Abrir portal HC" : "Abrir SIDCA-Receta";
  const hasHceUrl = !!hceUrl;

  const handleResolve = async () => {
    if (resolving || isResolved) return;
    const ok = window.confirm(
      "¿Confirmás que ya confeccionaste la receta en SIDCA? La solicitud se marcará como Resuelta."
    );
    if (!ok) return;
    setResolving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/consults/${consult.id}/resolve-receta`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ resolution_type: 'receta_sidca' }),
      });
      if (res.ok) {
        toast.success("Solicitud marcada como Resuelta");
        onResolved && onResolved();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || "No pudimos cerrar la solicitud");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error de conexión");
    } finally {
      setResolving(false);
    }
  };

  const handleRequiresConsult = async () => {
    if (reqSubmitting) return;
    setReqSubmitting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/consults/${consult.id}/requires-clinical-consult`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ note: reqNote.trim() || null }),
      });
      if (res.ok) {
        toast.success("Le avisamos al paciente que abra una consulta clínica");
        setShowReqModal(false);
        setReqNote("");
        onRequiresConsult && onRequiresConsult();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || "No pudimos enviar el aviso");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error de conexión");
    } finally {
      setReqSubmitting(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-xl border border-purple-200 p-6 space-y-4" data-testid="receta-actions-card">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
            Receta
          </span>
          <h2 className="text-lg font-semibold text-slate-900">Solicitud de receta</h2>
        </div>
        <p className="text-sm text-slate-600">
          {institution?.slug === 'fleni' || !institution
            ? <>La confección de la receta se hace en <strong>SIDCA-Receta</strong> (Fleni). Cuando la emitas, marcá esta solicitud como <em>Resuelta</em> para cerrar el loop.</>
            : <>Cuando emitas la receta en tu sistema habitual, marcá esta solicitud como <em>Resuelta</em> para cerrar el loop.</>
          }
        </p>

        {consult.medications_text && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Medicación solicitada</p>
            <pre className="text-sm text-slate-800 whitespace-pre-wrap break-words font-sans">
              {consult.medications_text}
            </pre>
          </div>
        )}

        {alreadyEscalatedToConsult && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            Ya le notificaste al paciente que esto requiere una consulta clínica.
          </div>
        )}

        {isResolved ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-800">
            <p className="font-semibold">Receta resuelta ✓</p>
            <p className="mt-1">
              Marcada el{" "}
              {consult.resolved_at ? new Date(consult.resolved_at).toLocaleString('es-AR') : '—'}.
            </p>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            {hasHceUrl && (
              <a
                href={hceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 text-sm font-medium text-primary border border-primary px-4 py-2 rounded-lg hover:bg-primary/5"
                data-testid="open-sidca-receta-btn"
              >
                {hceOpenLabel}
              </a>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowReqModal(true)}
              data-testid="receta-requires-consult-btn"
            >
              Esto requiere consulta
            </Button>
            <Button
              type="button"
              onClick={handleResolve}
              disabled={resolving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white sm:ml-auto"
              data-testid="receta-resolve-btn"
            >
              {resolving ? "Marcando..." : "Marcar como Resuelta"}
            </Button>
          </div>
        )}
      </div>

      {showReqModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="receta-requires-consult-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <h3 className="font-semibold text-slate-900">Marcar como &quot;Requiere consulta&quot;</h3>
            <p className="text-sm text-slate-600">
              El paciente recibirá un email indicando que abra una <strong>Consulta sobre tu tratamiento</strong>.
              Podés agregar un mensaje opcional (visible para el paciente).
            </p>
            <Textarea
              rows={4}
              placeholder="Mensaje al paciente (opcional). Ej: Necesito charlar con vos sobre el ajuste de dosis antes de renovar la receta."
              value={reqNote}
              onChange={(e) => setReqNote(e.target.value)}
              data-testid="receta-requires-consult-note"
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowReqModal(false)}>
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleRequiresConsult}
                disabled={reqSubmitting}
                data-testid="receta-requires-consult-submit"
              >
                {reqSubmitting ? "Enviando..." : "Enviar aviso"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const ConsultDetail = ({ user }) => {
  const { consultId } = useParams();
  const navigate = useNavigate();
  const [consult, setConsult] = useState(null);
  const [institution, setInstitution] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [escalation, setEscalation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [responseText, setResponseText] = useState("");
  const [responseFiles, setResponseFiles] = useState([]);
  const [showEscalationModal, setShowEscalationModal] = useState(false);
  const [showRequestInfoModal, setShowRequestInfoModal] = useState(false);
  const [doctorPreviewImage, setDoctorPreviewImage] = useState(null);
  const [doctorImageIndex, setDoctorImageIndex] = useState(0);
  const [showPatientHistory, setShowPatientHistory] = useState(false);
  const [previousConsultsCount, setPreviousConsultsCount] = useState(0);
  const [showWaiveModal, setShowWaiveModal] = useState(false);
  const [waiveReason, setWaiveReason] = useState("");
  const [waiving, setWaiving] = useState(false);
  // Close consult without responding and without charge
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeReason, setCloseReason] = useState("patient_resolved");
  const [closeNote, setCloseNote] = useState("");
  const [closing, setClosing] = useState(false);
  // Secretary billing notification (Fleni pilot shortcut)
  const [savedSecretaryEmail, setSavedSecretaryEmail] = useState("");
  const [notifySecretary, setNotifySecretary] = useState(false);
  const [secretaryEmailInput, setSecretaryEmailInput] = useState("");
  const [showSecretaryModal, setShowSecretaryModal] = useState(false);
  const [notifyingSecretary, setNotifyingSecretary] = useState(false);
  const [saveSecretaryDefault, setSaveSecretaryDefault] = useState(true);

  useEffect(() => {
    const loadSecretary = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch(`${API_URL}/api/doctors/me/secretary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.secretary_email) {
            setSavedSecretaryEmail(data.secretary_email);
            setSecretaryEmailInput(data.secretary_email);
            setNotifySecretary(true);
          }
        }
      } catch (e) {
        console.error('Error loading secretary contact:', e);
      }
    };
    loadSecretary();
  }, []);

  const loadConsult = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      
      // Load consult
      const response = await fetch(`${API_URL}/api/consults/${consultId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setConsult(data);
        if (data.response_text) {
          setResponseText(data.response_text);
        }

        // Load the consult's institution so tenant-specific resources (HC Digital URL,
        // support email, footer) can render dynamically instead of being hardcoded to Fleni.
        if (data.institution_id) {
          try {
            const instRes = await fetch(`${API_URL}/api/institutions/${data.institution_id}`);
            if (instRes.ok) setInstitution(await instRes.json());
          } catch (e) {
            console.error('Error loading institution:', e);
          }
        }

        // Count previous consults of the same patient (clinical continuity hint)
        if (data.patient_dni) {
          try {
            const histRes = await fetch(`${API_URL}/api/patients/${data.patient_dni}/history`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (histRes.ok) {
              const hist = await histRes.json();
              setPreviousConsultsCount((hist.consults || []).filter(c => c.id !== consultId).length);
            }
          } catch (e) {
            console.error('Error loading patient history count:', e);
          }
        }

        // Load attachments
        try {
          const attResponse = await fetch(`${API_URL}/api/consults/${consultId}/attachments`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (attResponse.ok) {
            setAttachments(await attResponse.json());
          }
        } catch (e) {
          console.error('Error loading attachments:', e);
        }

        // Load escalation info if escalated
        if (data.status === 'ESCALATED' && data.escalation_id) {
          try {
            const escResponse = await fetch(`${API_URL}/api/escalations/${data.escalation_id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (escResponse.ok) {
              setEscalation(await escResponse.json());
            }
          } catch (e) {
            console.error('Error loading escalation:', e);
          }
        }
      } else {
        toast.error("Error al cargar la consulta");
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Error al conectar con el servidor");
    } finally {
      setLoading(false);
    }
  }, [consultId, navigate]);

  useEffect(() => {
    loadConsult();
  }, [loadConsult]);

  const uploadResponseFiles = async (token) => {
    const uploadedUrls = [];
    
    for (const file of responseFiles) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('uploaded_by', 'doctor');
      
      try {
        const response = await fetch(`${API_URL}/api/attachments/upload?consult_id=${consultId}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          uploadedUrls.push(data.file_url);
        }
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    }
    
    return uploadedUrls;
  };

  const handleRespond = async () => {
    if (!responseText.trim()) {
      toast.error("La respuesta no puede estar vacía");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('auth_token');
      
      // Upload files first
      if (responseFiles.length > 0) {
        await uploadResponseFiles(token);
      }
      
      // Send response
      const response = await fetch(`${API_URL}/api/consults/${consultId}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          response_text: responseText,
          notify_secretary: notifySecretary && !!secretaryEmailInput.trim(),
          secretary_email: secretaryEmailInput.trim() || null,
          save_secretary_email: true,
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(
          data.secretary_notified
            ? "¡Respuesta enviada! Tu secretaría fue notificada para facturar."
            : "¡Respuesta enviada correctamente!"
        );
        if (data.secretary_notified && secretaryEmailInput.trim()) {
          setSavedSecretaryEmail(secretaryEmailInput.trim());
        }
        setResponseFiles([]);
        loadConsult();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al enviar la respuesta");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Error al conectar con el servidor");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEscalate = () => {
    setShowEscalationModal(true);
  };

  const handleWaive = async () => {
    setWaiving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/consults/${consultId}/waive`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ reason: waiveReason.trim() || null })
      });
      if (response.ok) {
        toast.success("Consulta marcada sin cargo. Se notificó al paciente.");
        setShowWaiveModal(false);
        setWaiveReason("");
        loadConsult();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al marcar sin cargo");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Error al conectar con el servidor");
    } finally {
      setWaiving(false);
    }
  };

  const handleCloseNoResponse = async () => {
    setClosing(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/consults/${consultId}/close-no-response`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ reason: closeReason, note: closeNote.trim() || null })
      });
      if (response.ok) {
        toast.success("Consulta cerrada sin cargo. Se notificó al paciente.");
        setShowCloseModal(false);
        setCloseNote("");
        navigate('/dashboard');
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al cerrar la consulta");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Error al conectar con el servidor");
    } finally {
      setClosing(false);
    }
  };

  const handleNotifySecretary = async () => {
    const email = secretaryEmailInput.trim();
    if (!email) {
      toast.error("Ingresá el correo de tu secretaría");
      return;
    }
    setNotifyingSecretary(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/consults/${consultId}/notify-secretary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email, save_as_default: saveSecretaryDefault })
      });
      if (response.ok) {
        toast.success(`Aviso enviado a ${email}`);
        if (saveSecretaryDefault) setSavedSecretaryEmail(email);
        setShowSecretaryModal(false);
        loadConsult();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al notificar a la secretaría");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Error al conectar con el servidor");
    } finally {
      setNotifyingSecretary(false);
    }
  };

  const handleRequestInfo = () => {
    setShowRequestInfoModal(true);
  };

  const handleRequestInfoSuccess = (data) => {
    loadConsult();
  };

  const handleEscalationSuccess = (data) => {
    setEscalation({
      escalation_type: data.escalation_type,
      urgency_level: data.urgency_level,
      recommended_timeframe: data.recommended_timeframe,
      created_at: new Date().toISOString()
    });
    loadConsult();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Cargando consulta...</p>
        </div>
      </div>
    );
  }

  if (!consult) return null;

  const isReceta = consult.category === 'recetas';
  const canRespond = !isReceta
    && ['Paid', 'InReview', 'INFO_RECEIVED'].includes(consult.status)
    && ['MEDICO', 'ADMIN'].includes(user.role);
  const hasRedFlags = consult.red_flags && Object.values(consult.red_flags).some(v => v);
  const patientAttachments = attachments.filter(a => a.uploaded_by !== 'doctor');
  const doctorAttachments = attachments.filter(a => a.uploaded_by === 'doctor');

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        {/* Header */}
        <nav className="border-b border-slate-200 bg-white/70 backdrop-blur-xl sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-14 sm:h-16">
              <div className="flex items-center space-x-2 sm:space-x-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/dashboard')}
                  data-testid="back-to-dashboard-btn"
                  className="px-2 sm:px-3"
                >
                  <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2" />
                  <span className="hidden sm:inline">Volver</span>
                </Button>
                <div className="hidden sm:flex items-center">
                  <img src="/logo-fleni.png" alt="Fleni" className="h-8 sm:h-10 object-contain" />
                </div>
              </div>
              <div className="text-xs sm:text-sm text-slate-600">
                <span className="hidden sm:inline">ID: </span>
                <code className="bg-slate-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-xs">{consult.id.slice(0, 8)}...</code>
              </div>
            </div>
          </div>
        </nav>

        {/* Urgent Escalation Banner */}
        {escalation && escalation.urgency_level === 'critica' && (
          <div className="bg-red-600 text-white py-3 px-4">
            <div className="max-w-5xl mx-auto flex items-center justify-center gap-3">
              <AlertTriangle className="h-5 w-5" />
              <span className="font-semibold">URGENCIA INMEDIATA</span>
              <span className="text-red-100">— Esta consulta fue escalada con urgencia crítica</span>
            </div>
          </div>
        )}

        <div className="max-w-5xl mx-auto px-4 py-8">
          {/* Compact status bar (replaces the old right-hand "Estado" card) */}
          <div className="mb-6 bg-white rounded-xl border border-slate-200 px-5 py-3 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="status-bar">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Estado</span>
              <Badge className={`${
                consult.status === 'Paid' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                consult.status === 'Responded' ? 'bg-green-50 text-green-700 border-green-200' :
                consult.status === 'Resolved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                consult.status === 'ESCALATED' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                consult.status === 'AWAITING_INFO' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                consult.status === 'INFO_RECEIVED' ? 'bg-cyan-50 text-cyan-700 border-cyan-200' :
                consult.status === 'CLOSED_TIMEOUT' ? 'bg-red-50 text-red-700 border-red-200' :
                consult.status === 'CLOSED_NO_RESPONSE' ? 'bg-slate-100 text-slate-600 border-slate-300' :
                'bg-slate-50 text-slate-700 border-slate-200'
              } border`}>
                {consult.status === 'Paid' ? 'Pendiente' :
                 consult.status === 'Responded' ? 'Respondida' :
                 consult.status === 'Resolved' ? 'Resuelta' :
                 consult.status === 'ESCALATED' ? 'Escalada' :
                 consult.status === 'AWAITING_INFO' ? 'Esperando info' :
                 consult.status === 'INFO_RECEIVED' ? 'Info recibida' :
                 consult.status === 'CLOSED_TIMEOUT' ? 'Cerrada' :
                 consult.status === 'CLOSED_NO_RESPONSE' ? 'Cerrada sin respuesta' :
                 consult.status === 'PendingPayment' ? 'Pago pendiente' :
                 consult.status === 'InReview' ? 'En revision' :
                 consult.status === 'RejectedByTriage' ? 'Rechazada por triaje' :
                 consult.status === 'UrgentBlocked' ? 'Bloqueada (urgente)' :
                 consult.status}
              </Badge>
            </div>
            {consult.sla_deadline && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-500">Plazo de respuesta</span>
                <span className="font-semibold text-slate-800">{new Date(consult.sla_deadline).toLocaleString('es-AR')}</span>
              </div>
            )}
            {consult.responded_at && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-500">Respondida</span>
                <span className="font-semibold text-slate-800">{new Date(consult.responded_at).toLocaleString('es-AR')}</span>
              </div>
            )}
            {consult.response_time_hours && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-500">Tiempo</span>
                <span className="font-semibold text-slate-800">
                  {consult.response_time_hours < 1
                    ? `${Math.round(consult.response_time_hours * 60)} min`
                    : `${Math.round(consult.response_time_hours)} h`}
                </span>
              </div>
            )}
          </div>

          {/* Escalation banner (full width) */}
          {(escalation || consult.status === 'ESCALATED') && (
            <div className="mb-6">
              <EscalationBlock escalation={escalation || {
                escalation_type: 'unknown',
                urgency_level: 'unknown',
                created_at: consult.updated_at || consult.created_at
              }} />
            </div>
          )}

          {/* Consult Details - full width */}
          <div className="space-y-6">
              {/* Third-party consult banner (Ley 25.326 / Ley 26.529) */}
              {consult.is_third_party && (
                <div className="bg-blue-50 border-l-4 border-blue-500 rounded-xl p-5" data-testid="third-party-banner">
                  <div className="flex items-start gap-3">
                    <User className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <h3 className="font-semibold text-blue-900">Consulta realizada por un tercero</h3>
                        <span className="inline-flex items-center text-[10px] uppercase tracking-wide bg-blue-600 text-white rounded px-2 py-0.5 font-semibold">
                          Autorizado
                        </span>
                      </div>
                      <div className="text-sm text-blue-900 space-y-1">
                        <p>
                          <strong>{consult.submitter_name || "—"}</strong>
                          {" "}realizó esta consulta en nombre del paciente{" "}
                          <strong>{consult.patient_name}</strong>.
                        </p>
                        <p className="text-xs text-blue-800">
                          Relación: {({
                            spouse: "Cónyuge / pareja",
                            parent: "Padre / madre",
                            child: "Hijo/a",
                            sibling: "Hermano/a",
                            caregiver_formal: "Cuidador/a formal",
                            other: consult.submitter_relationship_other || "Otra",
                          })[consult.submitter_relationship] || consult.submitter_relationship || "—"}
                          {" · "}DNI: {consult.submitter_dni || "—"}
                          {" · "}Email: {consult.submitter_email || "—"}
                        </p>
                        {consult.patient_authorization_accepted_at && (
                          <p className="text-xs text-blue-800">
                            ✓ Declaración de autorización aceptada el{" "}
                            {new Date(consult.patient_authorization_accepted_at).toLocaleString("es-AR")}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Patient Info */}
              <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-xl sm:text-2xl font-bold text-primary">Información del Paciente</h2>
                    {consult.payment_waived && (
                      <Badge
                        className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1"
                        data-testid="waived-badge"
                      >
                        <Ban className="h-3 w-3" />
                        Sin cargo
                      </Badge>
                    )}
                  </div>
                  {consult.patient_dni && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowPatientHistory(true)}
                      className={`w-full sm:w-auto ${previousConsultsCount > 0 ? 'text-blue-600 border-blue-300 bg-blue-50 hover:bg-blue-100 hover:text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}
                      data-testid="view-patient-history-btn"
                    >
                      <History className="h-4 w-4 mr-2" />
                      {previousConsultsCount > 0
                        ? `Consultas anteriores (${previousConsultsCount})`
                        : 'Ver historial'}
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center text-sm text-slate-600 mb-1">
                      <User className="h-4 w-4 mr-2" />
                      Nombre
                    </div>
                    <p className="font-semibold">{consult.patient_name}</p>
                  </div>
                  <div>
                    <div className="flex items-center text-sm text-slate-600 mb-1">
                      <Calendar className="h-4 w-4 mr-2" />
                      Email
                    </div>
                    <p className="font-semibold text-sm break-all">{consult.patient_email}</p>
                  </div>
                  {consult.patient_dni && (
                    <div>
                      <div className="flex items-center text-sm text-slate-600 mb-1">
                        <FileText className="h-4 w-4 mr-2" />
                        DNI
                      </div>
                      <p className="font-semibold">{consult.patient_dni}</p>
                    </div>
                  )}
                  {consult.obra_social && (
                    <div>
                      <div className="flex items-center text-sm text-slate-600 mb-1">
                        <CreditCard className="h-4 w-4 mr-2" />
                        Obra Social
                      </div>
                      <p className="font-semibold">{consult.obra_social}</p>
                    </div>
                  )}
                  {consult.credencial_number && (
                    <div>
                      <div className="flex items-center text-sm text-slate-600 mb-1">
                        <FileText className="h-4 w-4 mr-2" />
                        N° Credencial
                      </div>
                      <p className="font-semibold">{consult.credencial_number}</p>
                    </div>
                  )}
                  <div>
                    <div className="flex items-center text-sm text-slate-600 mb-1">
                      <Clock className="h-4 w-4 mr-2" />
                      Creada
                    </div>
                    <p className="font-semibold">{new Date(consult.created_at).toLocaleString('es-AR')}</p>
                  </div>
                  {consult.disposition && consult.disposition !== 'OK_ASYNC' && (
                    <div>
                      <div className="flex items-center text-sm text-slate-600 mb-1">
                        <AlertCircle className="h-4 w-4 mr-2" />
                        Disposición clínica
                      </div>
                      <Badge className={`${
                        consult.disposition === 'BLOCKED_URGENT' ? 'bg-red-100 text-red-700 border-red-200' :
                        consult.disposition === 'ESCALATED_BY_DOCTOR' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                        'bg-slate-100 text-slate-700 border-slate-200'
                      } border`}>
                        {consult.disposition === 'BLOCKED_URGENT' ? 'Bloqueada - Urgente' :
                         consult.disposition === 'ESCALATED_BY_DOCTOR' ? 'Escalada por médico' :
                         consult.disposition}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>

              {/* Red Flags Warning */}
              {hasRedFlags && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-red-900 mb-2">Banderas Rojas Detectadas</h3>
                      <ul className="space-y-1 text-sm text-red-800">
                        {Object.entries(consult.red_flags).filter(([_, v]) => v).map(([key, _]) => (
                          <li key={key}>• {key.replace('_', ' ')}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Past Red Flags — high clinical priority but NOT urgent.
                  The patient reported these symptoms but indicated they have already resolved. */}
              {consult.past_red_flags && consult.past_red_flags.length > 0 && (
                <div className="bg-amber-50 border-l-4 border-amber-500 rounded-xl p-6" data-testid="past-red-flags-warning">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="font-semibold text-amber-900">Alta prioridad clínica</h3>
                        <span className="inline-flex items-center text-[10px] uppercase tracking-wide bg-amber-600 text-white rounded px-2 py-0.5 font-semibold">
                          Síntoma de alarma reciente
                        </span>
                      </div>
                      <p className="text-sm text-amber-900 mb-2">
                        El paciente reportó haber tenido recientemente {consult.past_red_flags.length === 1 ? "el siguiente síntoma de alarma" : "los siguientes síntomas de alarma"}, pero indicó que ya están resueltos al momento de la consulta:
                      </p>
                      <ul className="space-y-1 text-sm text-amber-900 font-medium">
                        {consult.past_red_flags.map((pf, idx) => (
                          <li key={pf.key || idx} className="flex items-start gap-2">
                            <span className="text-amber-600 mt-0.5">•</span>
                            <span>{pf.label || pf.key}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="text-xs text-amber-800 mt-3 italic">
                        Te recomendamos prestar especial atención a este caso al elaborar la respuesta.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Triage Info */}
              {consult.triage_metadata && consult.triage_metadata.result && (
                <div className={`rounded-xl border p-6 ${
                  consult.triage_metadata.result.nivel === 3 ? 'bg-orange-50 border-orange-200' :
                  consult.triage_metadata.result.nivel === 2 ? 'bg-yellow-50 border-yellow-200' :
                  'bg-blue-50 border-blue-200'
                }`}>
                  <div className="flex items-start gap-3">
                    <Activity className={`h-6 w-6 flex-shrink-0 mt-0.5 ${
                      consult.triage_metadata.result.nivel === 3 ? 'text-orange-600' :
                      consult.triage_metadata.result.nivel === 2 ? 'text-yellow-600' :
                      'text-blue-600'
                    }`} />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className={`font-semibold ${
                          consult.triage_metadata.result.nivel === 3 ? 'text-orange-900' :
                          consult.triage_metadata.result.nivel === 2 ? 'text-yellow-900' :
                          'text-blue-900'
                        }`}>
                          Triaje: Nivel {consult.triage_metadata.result.nivel}
                        </h3>
                        <Badge className={`${
                          consult.triage_metadata.result.nivel === 3 ? 'bg-orange-100 text-orange-700 border-orange-300' :
                          consult.triage_metadata.result.nivel === 2 ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
                          'bg-blue-100 text-blue-700 border-blue-300'
                        } border`}>
                          {consult.triage_metadata.result.descripcion}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm mt-3">
                        <div>
                          <span className="text-slate-600">Inicio síntomas:</span>
                          <p className="font-medium">
                            {consult.triage_metadata.inicio_sintomas === 'menos_24h' ? 'Menos de 24h' :
                             consult.triage_metadata.inicio_sintomas === '1_2_dias' ? '1-2 días' :
                             'Más de 2 días'}
                          </p>
                        </div>
                        <div>
                          <span className="text-slate-600">Severidad:</span>
                          <p className="font-medium capitalize">{consult.triage_metadata.severidad}</p>
                        </div>
                        <div>
                          <span className="text-slate-600">SLA asignado:</span>
                          <p className="font-medium">{consult.triage_metadata.result.sla_hours}h</p>
                        </div>
                        <div>
                          <span className="text-slate-600">Acción:</span>
                          <p className="font-medium capitalize">{consult.triage_metadata.result.accion?.replace('_', ' ')}</p>
                        </div>
                      </div>
                      {consult.triage_metadata.result.sugerencia && (
                        <p className="mt-3 text-sm font-medium text-orange-700 bg-orange-100 px-3 py-2 rounded">
                          💡 {consult.triage_metadata.result.sugerencia}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Consult Reason */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="text-xl font-bold text-primary mb-4">Motivo de Consulta</h2>
                <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {consult.consult_reason}
                </p>
              </div>

              {/* Patient Attachments */}
              <PatientAttachmentsSection attachments={patientAttachments} />

              {/* Awaiting Info Block - Show when waiting for patient response */}
              <AwaitingInfoBlock consult={consult} />

              {/* Patient Additional Info Block - Show when patient has responded */}
              <PatientAdditionalInfoBlock consult={consult} />

              {/* Receta-specific actions (only for category === 'recetas') */}
              {isReceta && ['MEDICO', 'ADMIN'].includes(user.role) && (
                <RecetaActionsCard
                  consult={consult}
                  institution={institution}
                  onResolved={() => setConsult({ ...consult, status: 'Resolved', resolved_at: new Date().toISOString() })}
                  onRequiresConsult={() => setConsult({ ...consult, requires_clinical_consult: true })}
                />
              )}

              {/* Response Section */}
              {canRespond ? (
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h2 className="text-xl font-bold text-primary mb-4">Respuesta Médica</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="response">Escribe tu respuesta profesional *</Label>
                      <Textarea
                        id="response"
                        data-testid="response-textarea"
                        value={responseText}
                        onChange={(e) => setResponseText(e.target.value)}
                        placeholder="Escribe tu respuesta médica aquí..."
                        rows={10}
                        className="resize-none mt-2"
                      />
                      <p className="text-sm text-muted-foreground mt-2">
                        Esta respuesta será enviada al paciente por email
                      </p>
                    </div>

                    {/* Doctor File Upload */}
                    <div className="pt-4 border-t border-slate-200">
                      <DoctorFileUpload 
                        files={responseFiles}
                        onFilesChange={setResponseFiles}
                        disabled={submitting}
                      />
                    </div>

                    {/* Secretary billing shortcut — notify in the same action.
                        Hidden for Consultorio Privado (is_private_practice): private
                        doctors bill their own patients, there's no institutional secretary. */}
                    {!institution?.is_private_practice && (
                    <div className="pt-4 border-t border-slate-200" data-testid="secretary-notify-block">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notifySecretary}
                          onChange={(e) => setNotifySecretary(e.target.checked)}
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-[#1d366b] focus:ring-[#1d366b]"
                          data-testid="notify-secretary-checkbox"
                        />
                        <span>
                          <span className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                            <Receipt className="h-4 w-4 text-slate-500" />
                            Notificar a mi secretaría para facturación
                          </span>
                          <span className="block text-xs text-slate-500 mt-0.5">
                            Se envía un aviso administrativo (paciente, DNI, obra social y credencial, fecha y tipo de consulta). No incluye información clínica.
                          </span>
                        </span>
                      </label>
                      {notifySecretary && (
                        <div className="mt-3 pl-7">
                          <Label htmlFor="secretary-email" className="text-xs text-slate-600">
                            Correo de la secretaría
                          </Label>
                          <Input
                            id="secretary-email"
                            type="email"
                            value={secretaryEmailInput}
                            onChange={(e) => setSecretaryEmailInput(e.target.value)}
                            placeholder="secretaria@institucion.com"
                            className="mt-1.5 max-w-sm"
                            data-testid="secretary-email-input"
                          />
                          <p className="text-xs text-slate-400 mt-1">
                            {savedSecretaryEmail
                              ? "Guardado en tu perfil. Si lo cambiás, se usará este de ahora en más."
                              : "Se guardará en tu perfil para las próximas consultas."}
                          </p>
                        </div>
                      )}
                    </div>
                    )}

                    {/* Action Buttons:
                        - Primary "Enviar respuesta" on top, full-width
                        - Secondary "Solicitar Info" + "Escalar" sit side-by-side below on every viewport
                        (fixes mobile ordering bug where they stacked awkwardly above the main CTA). */}
                    <div className="pt-4 space-y-3">
                      <Button
                        onClick={handleRespond}
                        disabled={submitting || !responseText.trim()}
                        className="w-full h-12 rounded-full btn-hover"
                        data-testid="submit-response-btn"
                      >
                        {submitting ? "Enviando..." : "Enviar Respuesta"}
                        <Send className="ml-2 h-5 w-5 flex-shrink-0" />
                      </Button>

                      <div className="flex flex-row gap-2 sm:gap-3">
                        {/* Solicitar Info Button - Amber (only if not already requested and patient hasn't responded) */}
                        {!consult.info_request_sent_at && !consult.patient_additional_info && (
                          <div className="flex-1 flex items-center gap-1 min-w-0">
                            <Button
                              variant="outline"
                              onClick={handleRequestInfo}
                              className="flex-1 h-12 rounded-full border-amber-300 text-amber-600 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-400 px-3"
                              data-testid="request-info-btn"
                            >
                              <MessageSquarePlus className="h-4 w-4 mr-1.5 flex-shrink-0" />
                              <span className="truncate">Solicitar Info</span>
                            </Button>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className="p-2 text-amber-500 hover:text-amber-700 hover:bg-amber-50 rounded-full transition-colors flex-shrink-0"
                                  aria-label="Información sobre solicitar info"
                                >
                                  <Info className="h-5 w-5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                className="max-w-[280px] bg-slate-800 text-white text-sm p-3"
                              >
                                <p>
                                  <strong>Solicitar información</strong> te permite pedir aclaraciones al paciente antes de responder.
                                  Solo puede usarse <strong>una vez</strong> por consulta.
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        )}

                        {/* Escalar Button - Blue with Tooltip */}
                        <div className="flex-1 flex items-center gap-1 min-w-0">
                          <Button
                            variant="outline"
                            onClick={handleEscalate}
                            className="flex-1 h-12 rounded-full border-blue-300 text-blue-600 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-400 px-3"
                            data-testid="escalate-btn"
                          >
                            <ExternalLink className="h-4 w-4 mr-1.5 flex-shrink-0" />
                            <span className="truncate">Escalar</span>
                          </Button>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors flex-shrink-0"
                                aria-label="Información sobre escalar"
                              >
                                <Info className="h-5 w-5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent
                              side="top"
                              className="max-w-[280px] bg-slate-800 text-white text-sm p-3"
                            >
                              <p>
                                <strong>Escalar</strong> significa que la consulta requiere una evaluación más completa y será derivada a una teleconsulta o consulta presencial.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </div>

                      {/* Waiver — mark consult as no-cost (doctor discretionary) */}
                      {consult.payment_waived ? (
                        <div
                          className="flex items-center justify-center gap-2 h-11 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-medium"
                          data-testid="waived-indicator"
                        >
                          <CheckCircle2 className="h-4 w-4" />
                          Consulta marcada sin cargo
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            onClick={() => setShowWaiveModal(true)}
                            className="flex-1 h-11 rounded-full text-slate-500 hover:text-emerald-700 hover:bg-emerald-50"
                            data-testid="waive-btn"
                          >
                            <Ban className="h-4 w-4 mr-1.5 flex-shrink-0" />
                            <span className="truncate">Waiver — consulta sin cargo</span>
                          </Button>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="p-2 text-slate-400 hover:text-emerald-700 hover:bg-emerald-50 rounded-full transition-colors flex-shrink-0"
                                aria-label="Información sobre waiver"
                              >
                                <Info className="h-5 w-5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[280px] bg-slate-800 text-white text-sm p-3">
                              <p>
                                <strong>Waiver</strong> marca esta consulta como <strong>sin cargo</strong>. El paciente
                                será notificado y, si tenía un importe retenido, se liberará. Útil para consultas
                                administrativas o cuando decidís no cobrar.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      )}

                      {/* Close without responding (no charge) — discreet link with tooltip */}
                      {!consult.payment_waived && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={() => setShowCloseModal(true)}
                              className="mt-1 mx-auto block text-xs text-slate-400 hover:text-slate-600 underline underline-offset-2 transition-colors"
                              data-testid="close-no-response-btn"
                            >
                              Cerrar sin responder (sin cargo)
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[300px] bg-slate-800 text-white text-sm p-3">
                            <p>
                              <strong>Cierra y archiva</strong> esta consulta <strong>sin responder y sin cargo</strong>.
                              Úsalo cuando ya no corresponde responder (el paciente lo resolvió por otra vía,
                              ya no es paciente, duplicada, etc.). Sale de tu bandeja, se avisa al paciente y queda
                              en el historial.
                              <br /><br />
                              <span className="text-slate-300">A diferencia del <strong>Waiver</strong> (sin cargo pero igual respondés), acá no se envía ninguna respuesta médica.</span>
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      )}

                    </div>
                  </div>
                </div>
              ) : (
                consult.response_text && (
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h2 className="text-xl font-bold text-primary mb-4">Respuesta Médica</h2>
                    <p className="text-slate-700 leading-relaxed whitespace-pre-wrap mb-4">
                      {consult.response_text}
                    </p>

                    {/* Secretary billing notification — backup action after responding.
                        Hidden for Consultorio Privado (is_private_practice). */}
                    {!institution?.is_private_practice && (
                    <div className="mb-4 pb-4 border-b border-slate-200" data-testid="secretary-billing-section">
                      {consult.billing_notified ? (
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
                          <div className="flex items-start gap-2 text-sm text-slate-700" data-testid="billing-notified-indicator">
                            <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                            <span>
                              Secretaría notificada a <strong>{consult.billing_notified_to}</strong>
                              {consult.billing_notified_at && (
                                <span className="text-slate-500">
                                  {" "}· {new Date(consult.billing_notified_at).toLocaleString('es-AR')}
                                </span>
                              )}
                            </span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setSecretaryEmailInput(consult.billing_notified_to || savedSecretaryEmail); setShowSecretaryModal(true); }}
                            className="text-xs text-slate-500 hover:text-[#1d366b]"
                            data-testid="resend-secretary-btn"
                          >
                            Reenviar
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          onClick={() => { setSecretaryEmailInput(savedSecretaryEmail); setShowSecretaryModal(true); }}
                          className="w-full sm:w-auto h-11 rounded-full border-[#1d366b]/30 text-[#1d366b] hover:bg-[#1d366b]/5"
                          data-testid="notify-secretary-btn"
                        >
                          <Receipt className="h-4 w-4 mr-2" />
                          Notificar a mi secretaría
                        </Button>
                      )}
                    </div>
                    )}
                    
                    {/* Doctor attachments in response */}
                    {doctorAttachments.length > 0 && (() => {
                      const doctorImages = doctorAttachments.filter(att => 
                        att.file_type?.startsWith('image/') || 
                        /\.(jpg|jpeg|png|gif|webp)$/i.test(att.filename || att.original_filename || att.file_url)
                      );
                      const doctorOtherFiles = doctorAttachments.filter(att => 
                        !att.file_type?.startsWith('image/') && 
                        !/\.(jpg|jpeg|png|gif|webp)$/i.test(att.filename || att.original_filename || att.file_url)
                      );
                      
                      const handleDoctorImageClick = (att) => {
                        const idx = doctorImages.findIndex(img => img.id === att.id);
                        setDoctorImageIndex(idx >= 0 ? idx : 0);
                        setDoctorPreviewImage(att);
                      };
                      
                      const getImageUrl = (att) => att.file_url?.startsWith('http') ? att.file_url : `${API_URL}${att.file_url}`;
                      
                      return (
                        <div className="mb-4 pt-4 border-t border-slate-200">
                          <h4 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
                            <Paperclip className="h-4 w-4" />
                            Archivos adjuntos del médico
                            <Badge className="bg-slate-100 text-slate-500 border-0 text-xs">{doctorAttachments.length}</Badge>
                          </h4>
                          
                          {/* Doctor Images Gallery */}
                          {doctorImages.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs text-slate-400 mb-2 flex items-center gap-1">
                                <Image className="h-3 w-3" />
                                Imágenes - Clic para ampliar
                              </p>
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {doctorImages.map((att, idx) => (
                                  <AttachmentItem 
                                    key={att.id || idx} 
                                    attachment={att}
                                    onImageClick={handleDoctorImageClick}
                                    showThumbnail={true}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Doctor Other Files */}
                          {doctorOtherFiles.length > 0 && (
                            <div className={doctorImages.length > 0 ? "pt-2" : ""}>
                              <div className="space-y-2">
                                {doctorOtherFiles.map((att, idx) => (
                                  <AttachmentItem key={att.id || idx} attachment={att} />
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Doctor Image Preview Modal */}
                          <ImagePreviewModal
                            isOpen={!!doctorPreviewImage}
                            onClose={() => setDoctorPreviewImage(null)}
                            imageUrl={doctorPreviewImage ? getImageUrl(doctorPreviewImage) : ''}
                            filename={doctorPreviewImage?.filename || doctorPreviewImage?.original_filename || 'Imagen'}
                            allImages={doctorImages}
                            currentIndex={doctorImageIndex}
                            onNavigate={(newIdx) => {
                              if (newIdx >= 0 && newIdx < doctorImages.length) {
                                setDoctorImageIndex(newIdx);
                                setDoctorPreviewImage(doctorImages[newIdx]);
                              }
                            }}
                          />
                        </div>
                      );
                    })()}
                    
                    {/* Consolidated text for clinical history */}
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-slate-600">Resumen para Historia Clínica</h4>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          data-testid="copy-hc-btn"
                          onClick={() => {
                            const text = `MOTIVO DE CONSULTA:\n${consult.consult_reason}\n\nRESPUESTA MÉDICA:\n${consult.response_text}`;
                            navigator.clipboard.writeText(text);
                            toast.success("Texto copiado al portapapeles");
                          }}
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          Copiar
                        </Button>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-700 space-y-3 border border-slate-200">
                        <div>
                          <span className="font-semibold text-slate-500 text-xs uppercase tracking-wide">Motivo de consulta</span>
                          <p className="mt-1 whitespace-pre-wrap">{consult.consult_reason}</p>
                        </div>
                        <hr className="border-slate-200" />
                        <div>
                          <span className="font-semibold text-slate-500 text-xs uppercase tracking-wide">Respuesta médica</span>
                          <p className="mt-1 whitespace-pre-wrap">{consult.response_text}</p>
                        </div>
                      </div>
                      {/* HC Digital link:
                          - Every institutional tenant shows it (with its own
                            hce_url if configured in contact_info, or Fleni's
                            SIDCA as fallback while contact_info is the only
                            configured tenant).
                          - Consultorio Privado tenants (is_private_practice)
                            don't have institutional HC, so we omit the link.
                          Also renders when `institution` is still null
                          (initial fetch pending) to prevent the link from
                          "disappearing" while the page mounts. */}
                      {(!institution || !institution.is_private_practice) && (
                        <a
                          href={institution?.contact_info?.hce_url || DEFAULT_HCE_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 inline-flex items-center gap-2 text-sm text-[#1d366b] hover:underline font-medium"
                          data-testid="link-hc-digital"
                        >
                          <ExternalLink className="h-4 w-4" />
                          {institution?.contact_info?.hce_label || DEFAULT_HCE_LABEL}
                        </a>
                      )}
                    </div>
                  </div>
                )
              )}

              {/* Consolidated clinical-history summary for ESCALATED consults.
                  An escalated consult has no response_text, so the answered-state
                  summary above never renders — leaving the doctor without the HC
                  access link. Mirror that block here (institutional tenants only;
                  Consultorio Privado has no institutional HC). */}
              {consult.status === 'ESCALATED' && (!institution || !institution.is_private_practice) && (
                <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="escalation-hc-summary">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-slate-600">Resumen para Historia Clínica</h4>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      data-testid="copy-hc-escalation-btn"
                      onClick={() => {
                        const motivo = consult.consult_reason || '';
                        const der = escalation?.clinical_reason
                          ? `\n\nMOTIVO DE DERIVACIÓN:\n${escalation.clinical_reason}`
                          : '';
                        navigator.clipboard.writeText(`MOTIVO DE CONSULTA:\n${motivo}${der}\n\nDISPOSICIÓN: Consulta escalada / derivada por el médico.`);
                        toast.success("Texto copiado al portapapeles");
                      }}
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Copiar
                    </Button>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-700 space-y-3 border border-slate-200">
                    <div>
                      <span className="font-semibold text-slate-500 text-xs uppercase tracking-wide">Motivo de consulta</span>
                      <p className="mt-1 whitespace-pre-wrap">{consult.consult_reason}</p>
                    </div>
                    {escalation?.clinical_reason && (
                      <>
                        <hr className="border-slate-200" />
                        <div>
                          <span className="font-semibold text-slate-500 text-xs uppercase tracking-wide">Motivo de derivación</span>
                          <p className="mt-1 whitespace-pre-wrap">{escalation.clinical_reason}</p>
                        </div>
                      </>
                    )}
                  </div>
                  <a
                    href={institution?.contact_info?.hce_url || DEFAULT_HCE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-2 text-sm text-[#1d366b] hover:underline font-medium"
                    data-testid="link-hc-digital"
                  >
                    <ExternalLink className="h-4 w-4" />
                    {institution?.contact_info?.hce_label || DEFAULT_HCE_LABEL}
                  </a>
                </div>
              )}
            </div>
        </div>

        {/* Escalation Modal */}
        <EscalationModal
          isOpen={showEscalationModal}
          onClose={() => setShowEscalationModal(false)}
          consultId={consultId}
          onSuccess={handleEscalationSuccess}
        />

        {/* Request Info Modal */}
        <RequestInfoModal
          isOpen={showRequestInfoModal}
          onClose={() => setShowRequestInfoModal(false)}
          consultId={consultId}
          onSuccess={handleRequestInfoSuccess}
          alreadyRequested={!!consult?.info_request_sent_at}
        />
        
        {consult && (
          <PatientHistoryPanel
            patientDni={consult.patient_dni}
            patientName={consult.patient_name}
            currentConsultId={consultId}
            isOpen={showPatientHistory}
            onClose={() => setShowPatientHistory(false)}
            onUseResponse={(text) => setResponseText(text)}
          />
        )}

        {/* Waiver Confirmation Modal */}
        {showWaiveModal && (
          <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" data-testid="waive-modal">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="h-11 w-11 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <Ban className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Marcar consulta sin cargo</h2>
                  <p className="text-sm text-slate-500 mt-0.5">El paciente será notificado que la consulta no tuvo costo.</p>
                </div>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-emerald-800">
                  Si el paciente tenía un importe retenido, será liberado. No se realizará ningún cobro por esta consulta.
                </p>
              </div>

              <Label className="text-sm text-slate-700">Motivo (opcional, interno)</Label>
              <Textarea
                value={waiveReason}
                onChange={(e) => setWaiveReason(e.target.value)}
                placeholder="Ej: Consulta administrativa (no consigue la marca del medicamento)."
                rows={3}
                maxLength={280}
                className="resize-none mt-1.5"
                data-testid="waive-reason-textarea"
              />

              <div className="flex gap-3 mt-5">
                <Button
                  variant="outline"
                  onClick={() => { setShowWaiveModal(false); setWaiveReason(""); }}
                  className="flex-1"
                  disabled={waiving}
                  data-testid="waive-cancel-btn"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleWaive}
                  disabled={waiving}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="waive-confirm-btn"
                >
                  {waiving ? "Marcando..." : "Confirmar sin cargo"}
                </Button>
              </div>
            </div>
          </div>
        )}
        {/* Close Without Responding Modal */}
        {showCloseModal && (
          <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" data-testid="close-no-response-modal">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="h-11 w-11 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <X className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Cerrar sin responder</h2>
                  <p className="text-sm text-slate-500 mt-0.5">La consulta se cierra sin cargo y sin respuesta médica. Se notificará al paciente.</p>
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-slate-700">
                  La consulta saldrá de tu bandeja de pendientes y quedará en el historial como <strong>"Cerrada sin respuesta"</strong>. No se realizará ningún cobro.
                </p>
              </div>

              <Label className="text-sm text-slate-700">Motivo del cierre *</Label>
              <select
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                className="w-full mt-1.5 h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
                data-testid="close-reason-select"
              >
                <option value="patient_resolved">El paciente lo resolvió por otra vía</option>
                <option value="not_patient">Ya no es paciente</option>
                <option value="duplicate">Consulta duplicada</option>
                <option value="other">Otro</option>
              </select>

              <Label className="text-sm text-slate-700 mt-4 block">Nota (opcional, interna)</Label>
              <Textarea
                value={closeNote}
                onChange={(e) => setCloseNote(e.target.value)}
                placeholder="Detalle opcional para el registro de auditoría."
                rows={2}
                maxLength={280}
                className="resize-none mt-1.5"
                data-testid="close-note-textarea"
              />

              <div className="flex gap-3 mt-5">
                <Button
                  variant="outline"
                  onClick={() => { setShowCloseModal(false); setCloseNote(""); }}
                  className="flex-1"
                  disabled={closing}
                  data-testid="close-cancel-btn"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleCloseNoResponse}
                  disabled={closing}
                  className="flex-1 bg-slate-700 hover:bg-slate-800 text-white"
                  data-testid="close-confirm-btn"
                >
                  {closing ? "Cerrando..." : "Cerrar sin cargo"}
                </Button>
              </div>
            </div>
          </div>
        )}
        {/* Secretary Billing Notification Modal */}
        {showSecretaryModal && (
          <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" data-testid="secretary-modal">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="h-11 w-11 rounded-full bg-[#1d366b]/10 flex items-center justify-center flex-shrink-0">
                  <Receipt className="h-5 w-5 text-[#1d366b]" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Notificar a mi secretaría</h2>
                  <p className="text-sm text-slate-500 mt-0.5">
                    Se envía un aviso para generar el episodio de facturación.
                  </p>
                </div>
              </div>

              <div className={`border rounded-lg p-3 mb-4 text-sm ${consult.payment_waived ? 'bg-red-50 border-red-200 text-red-800' : 'bg-blue-50 border-blue-200 text-blue-900'}`}>
                {consult.payment_waived
                  ? "Esta consulta está marcada sin cargo: el aviso se enviará con la etiqueta NO FACTURAR."
                  : "El aviso incluye N° de consulta, paciente, DNI, obra social y credencial, fecha y tipo de consulta. No incluye información clínica."}
              </div>

              <Label htmlFor="secretary-modal-email" className="text-sm text-slate-700">
                Correo de la secretaría
              </Label>
              <Input
                id="secretary-modal-email"
                type="email"
                value={secretaryEmailInput}
                onChange={(e) => setSecretaryEmailInput(e.target.value)}
                placeholder="secretaria@institucion.com"
                className="mt-1.5"
                data-testid="secretary-modal-email-input"
              />
              <label className="flex items-center gap-2 mt-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveSecretaryDefault}
                  onChange={(e) => setSaveSecretaryDefault(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-[#1d366b] focus:ring-[#1d366b]"
                  data-testid="save-secretary-default-checkbox"
                />
                <span className="text-sm text-slate-600">Guardar como predeterminado</span>
              </label>

              <div className="flex gap-3 mt-5">
                <Button
                  variant="outline"
                  onClick={() => setShowSecretaryModal(false)}
                  className="flex-1"
                  disabled={notifyingSecretary}
                  data-testid="secretary-cancel-btn"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleNotifySecretary}
                  disabled={notifyingSecretary || !secretaryEmailInput.trim()}
                  className="flex-1"
                  data-testid="secretary-confirm-btn"
                >
                  {notifyingSecretary ? "Enviando..." : "Enviar aviso"}
                  <Mail className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default ConsultDetail;
